"use client"

import { useState, useEffect, useContext } from "react"
import { Link } from "react-router-dom"
import axios from "axios"
import { API_URL } from "../config"
import { AuthContext } from "../context/AuthContext"
import { useLanguage } from "../context/LanguageContext"
import "./GroupManagement.css"

const GroupManagement = () => {
  const { user } = useContext(AuthContext)
  const { t, direction } = useLanguage()
  const [groups, setGroups] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [searchTerm, setSearchTerm] = useState("")
  const [sortBy, setSortBy] = useState("name")

  useEffect(() => {
    fetchGroups()
  }, [])

  const fetchGroups = async () => {
    try {
      setLoading(true)
      const token = localStorage.getItem("token")
      const response = await axios.get(`${API_URL}/api/groups`, {
        headers: { "x-auth-token": token },
      })
      setGroups(response.data)
      setLoading(false)
    } catch (err) {
      setError("Failed to load parameter Stations")
      setLoading(false)
      console.error(err)
    }
  }

  const handleDeleteGroup = async (groupId) => {
    if (window.confirm(t("Are you sure you want to delete this parameter Station?"))) {
      try {
        const token = localStorage.getItem("token")
        await axios.delete(`${API_URL}/api/groups/${groupId}`, {
          headers: { "x-auth-token": token },
        })
        setGroups(groups.filter((group) => group._id !== groupId))
      } catch (err) {
        setError("Failed to delete parameter Station")
        console.error(err)
      }
    }
  }

  const filteredGroups = groups
    .filter(
      (group) =>
        group.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        group.groupId.toLowerCase().includes(searchTerm.toLowerCase()),
    )
    .sort((a, b) => {
      if (sortBy === "name") return a.name.localeCompare(b.name)
      if (sortBy === "groupId") return a.groupId.localeCompare(b.groupId)
      if (sortBy === "devices") return (b.selectedDevices?.length || 0) - (a.selectedDevices?.length || 0)
      return 0
    })

  if (loading) {
    return (
      <div className="group-management-loading">
        <div className="spinner"></div>
        <p>{t("Loading parameter Stations...")}</p>
      </div>
    )
  }

  return (
    <div className="group-management-container" dir={direction}>
      <div className="group-management-header">
        <h1>{t("Parameter Stations Management")}</h1>
        <Link to="/group" className="btn btn-primary">
          <i className="fas fa-plus"></i> {t("Add New Station")}
        </Link>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      <div className="group-management-controls">
        <div className="search-box">
          <i className="fas fa-search"></i>
          <input
            type="text"
            placeholder={t("Search Stations...")}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="sort-controls">
          <label>{t("Sort by")}:</label>
          <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
            <option value="name">{t("Name")}</option>
            <option value="groupId">{t("Station ID")}</option>
            <option value="devices">{t("Device Count")}</option>
          </select>
        </div>
      </div>

      <div className="groups-grid">
  {filteredGroups.length === 0 ? (
    <div className="empty-state">
      <i className="fas fa-layer-group empty-icon"></i>
      <h3>{t("No parameter Stations found")}</h3>
      <p>{searchTerm ? t("Try adjusting your search") : t("Create your first parameter Station")}</p>
      <Link to="/group" className="btn btn-primary">
        {t("Add Parameter Station")}
      </Link>
    </div>
  ) : (
    filteredGroups.map((group) => {
      const lat = group.location?.coordinates[1] || 0;
      const lng = group.location?.coordinates[0] || 0;
      const deviceCount = group.selectedDevices?.length || 0;
      const deviceParamsCount = group.selectedDevices?.reduce((acc, device) => acc + (device.selectedParameters?.length || 0), 0);
      const apiParamsCount = group.apiParams?.length || 0;

      return (
        <div key={group._id} className="group-card">
          <div className="group-card-header">
            <h3><i className="fas fa-tag"></i> {t("Station Name")}: {group.name}</h3>
            <span className="group-id"><i className="fas fa-id-badge"></i> ID: {group.groupId}</span>
          </div>

          <div className="group-card-body">
            <div className="group-info">
              <div className="info-item">
                <i className="fas fa-map-marker-alt"></i>
                <span>{t("Location")}: {lat.toFixed(4)}, {lng.toFixed(4)}</span>
              </div>
              <div className="info-item">
                <i className="fas fa-microchip"></i>
                <span>{t("Devices")}: {deviceCount}</span>
              </div>
              <div className="info-item">
                <i className="fas fa-cogs"></i>
                <span>{t("Device Parameters")}: {deviceParamsCount}</span>
              </div>
              <div className="info-item">
                <i className="fas fa-cloud"></i>
                <span>{t("Forecasting Parameters")}: {apiParamsCount}</span>
              </div>
            </div>
          </div>

          <div className="group-card-actions">
            <Link to={`/groups/${group._id}`} className="btn btn-outline" title={t("View Details")}>
              <i className="fas fa-eye"></i>
            </Link>
            <Link to={`/groups/${group._id}/edit`} className="btn btn-outline" title={t("Edit Station")}>
              <i className="fas fa-edit"></i>
            </Link>
            <button
              onClick={() => handleDeleteGroup(group._id)}
              className="btn btn-outline btn-danger"
              title={t("Delete Station")}
            >
              <i className="fas fa-trash"></i>
            </button>
          </div>
        </div>
      );
    })
  )}
</div>


      <div className="groups-summary">
        <p>
          {t("Total")}: {groups.length} {t("parameter Stations")} | {t("Showing")}: {filteredGroups.length}
        </p>
      </div>
    </div>
  )
}

export default GroupManagement
